import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Slider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { Add, Delete, Warning } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { setTradings } from '../../../request/Analysis/hitorySlice';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#1E2329',
    color: 'white',
    fontSize: '12px',
    height: '32px',
    '& fieldset': { borderColor: '#2B3139' },
    '&:hover fieldset': { borderColor: '#F7A600' },
    '&.Mui-focused fieldset': { borderColor: '#F7A600' }
  },
  '& .MuiInputLabel-root': { 
    color: '#848E9C', 
    fontSize: '11px',
    '&.Mui-focused': { color: '#F7A600' }
  },
  '& .MuiOutlinedInput-input': {
    padding: '6px 12px',
    fontSize: '12px'
  }
}));

const PriceDisplay = styled(Box)(({ theme }) => ({
  backgroundColor: '#181A20',
  border: '1px solid #2B3139',
  borderRadius: '4px',
  padding: '8px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '32px'
}));

const ScaledOrder = ({ orderType, balance, coinPrice, currentPair, tradingType }) => {
  const dispatch = useDispatch();
  const { tradingPlans } = useSelector((state) => state.plans);
  const { user } = useSelector((state) => state.auth);
  
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [quantity, setQuantity] = useState(50);
  const [quantitySlider, setQuantitySlider] = useState(0);
  const [scaledOrders, setScaledOrders] = useState([]);
  const [postOnly, setPostOnly] = useState(false);
  const [reduceOnly, setReduceOnly] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [orderValue, setOrderValue] = useState(0);

  // Improved decimal formatting
  const formatPrice = (value) => parseFloat(value || 0).toFixed(2);
  const formatQuantity = (value) => parseFloat(value || 0).toFixed(4);
  const formatBalance = (value) => parseFloat(value || 0).toFixed(2);

  const calculatePriceRange = () => {
    const currentPrice = parseFloat(coinPrice || 0);
    if (orderType === 'Buy') {
      return {
        min: formatPrice(currentPrice * 0.95),
        max: formatPrice(currentPrice * 0.99)
      };
    } else {
      return {
        min: formatPrice(currentPrice * 1.01),
        max: formatPrice(currentPrice * 1.05)
      };
    }
  };

  const priceRange = calculatePriceRange();

  const calculateOrderDistribution = () => {
    if (!minPrice || !maxPrice) return [];
    
    const min = parseFloat(minPrice);
    const max = parseFloat(maxPrice);
    const orderCount = 5; // Default number of scaled orders
    const priceStep = (max - min) / (orderCount - 1);
    const quantityPerOrder = quantity / orderCount;
    
    const orders = [];
    for (let i = 0; i < orderCount; i++) {
      const price = min + (priceStep * i);
      orders.push({
        id: i + 1,
        price: formatPrice(price),
        quantity: formatQuantity(quantityPerOrder),
        value: formatPrice(price * quantityPerOrder)
      });
    }
    
    return orders;
  };

  const distributedOrders = calculateOrderDistribution();

  const addScaledOrders = () => {
    if (distributedOrders.length > 0) {
      setScaledOrders([...scaledOrders, ...distributedOrders]);
    }
  };

  const removeOrder = (index) => {
    const newOrders = scaledOrders.filter((_, i) => i !== index);
    setScaledOrders(newOrders);
  };

  const getSelectedPlan = () => {
    if (!tradingPlans || !Array.isArray(tradingPlans)) return null;
    return tradingPlans.find(plan => plan.id === parseInt(selectedDuration));
  };

  const selectedPlan = getSelectedPlan();

  const calculateOrderValue = () => {
    if (!scaledOrders.length) return 0;
    const total = scaledOrders.reduce((sum, order) => sum + parseFloat(order.value), 0);
    return total;
  };

  useEffect(() => {
    const value = calculateOrderValue();
    setOrderValue(value);
  }, [scaledOrders]);

  const validateForm = () => {
    if (!selectedDuration) {
      setError('Please select a duration');
      return false;
    }
    if (scaledOrders.length === 0) {
      setError('Please add at least one scaled order');
      return false;
    }
    if (!minPrice || !maxPrice) {
      setError('Please set both minimum and maximum prices');
      return false;
    }
    if (parseFloat(minPrice) >= parseFloat(maxPrice)) {
      setError('Minimum price must be less than maximum price');
      return false;
    }
    if (orderValue > parseFloat(balance)) {
      setError('Order value exceeds available balance');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setShowConfirmation(true);
  };

  const confirmOrder = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const orderData = {
        user_id: user?.id,
        trading_type: tradingType,
        order_type: 'ScaledOrder',
        side: orderType,
        symbol: currentPair,
        quantity: quantity,
        min_price: parseFloat(minPrice),
        max_price: parseFloat(maxPrice),
        order_count: scaledOrders.length,
        total_value: orderValue,
        post_only: postOnly,
        reduce_only: reduceOnly,
        duration: selectedDuration,
        duration_name: selectedPlan?.name || '',
        orders: scaledOrders,
        status: 'pending',
        created_at: new Date().toISOString()
      };

      const response = await fetch('/api/trading/store', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(orderData)
      });

      const result = await response.json();

      if (response.ok) {
        // Add to Redux store
        dispatch(setTradings([result.data]));
        
        // Reset form
        setScaledOrders([]);
        setMinPrice('');
        setMaxPrice('');
        setQuantity(50);
        setQuantitySlider(0);
        setPostOnly(false);
        setReduceOnly(false);
        setSelectedDuration('');
        setOrderValue(0);
        
        setShowConfirmation(false);
      } else {
        setError(result.message || 'Failed to place order');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelOrder = () => {
    setShowConfirmation(false);
    setError('');
  };

  const handleQuantitySliderChange = (event, newValue) => {
    setQuantitySlider(newValue);
    if (balance && coinPrice) {
      const maxQty = parseFloat(balance) / parseFloat(coinPrice);
      const newQuantity = (maxQty * newValue) / 100;
      setQuantity(newQuantity);
    }
  };

  const handlePercentageClick = (percentage) => {
    setQuantitySlider(percentage);
    if (balance && coinPrice) {
      const maxQty = parseFloat(balance) / parseFloat(coinPrice);
      const newQuantity = (maxQty * percentage) / 100;
      setQuantity(newQuantity);
    }
  };

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Error Display */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 1.5, fontSize: '11px' }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {/* Available Balance */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, px: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px' }}>
          Available
        </Typography>
        <Typography variant="caption" sx={{ color: 'white', fontSize: '11px', fontWeight: 500 }}>
          {formatBalance(balance)} USDT
        </Typography>
      </Box>

      {/* Duration Selection */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Duration
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={selectedDuration}
            onChange={(e) => setSelectedDuration(e.target.value)}
            displayEmpty
            sx={{
              backgroundColor: '#1E2329',
              color: 'white',
              fontSize: '12px',
              height: '32px',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2B3139' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600' },
              '& .MuiSelect-icon': { color: '#848E9C' }
            }}
          >
            <MenuItem value="" disabled>
              <Typography sx={{ color: '#848E9C', fontSize: '12px' }}>
                Select Duration
              </Typography>
            </MenuItem>
            {tradingPlans && Array.isArray(tradingPlans) && tradingPlans.map((plan) => (
              <MenuItem key={plan.id} value={plan.id}>
                <Typography sx={{ color: 'white', fontSize: '12px' }}>
                  {plan.duration}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Price Range */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Price Range
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px', mb: 0.5, display: 'block' }}>
              Min. Price
            </Typography>
            <StyledTextField
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              placeholder={priceRange.min}
              size="small"
              fullWidth
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px', mb: 0.5, display: 'block' }}>
              Max. Price
            </Typography>
            <StyledTextField
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder={priceRange.max}
              size="small"
              fullWidth
            />
          </Box>
        </Box>
      </Box>

      {/* Quantity Slider */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Quantity ({currentPair})
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ color: 'white', fontSize: '14px', mr: 1, fontWeight: 500 }}>
            {formatQuantity(quantity)}
          </Typography>
          <Typography sx={{ color: '#848E9C', fontSize: '12px' }}>
            {currentPair}
          </Typography>
        </Box>
        
        <Slider
          value={quantitySlider}
          onChange={handleQuantitySliderChange}
          min={0}
          max={100}
          size="small"
          sx={{
            color: '#F7A600',
            height: 4,
            '& .MuiSlider-track': { backgroundColor: '#F7A600' },
            '& .MuiSlider-thumb': { 
              backgroundColor: '#F7A600',
              width: 12,
              height: 12,
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 8px rgba(247, 166, 0, 0.16)'
              }
            }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          {[0, 25, 50, 75, 100].map(value => (
            <Typography 
              key={value} 
              variant="caption" 
              sx={{ 
                color: '#848E9C', 
                fontSize: '9px',
                cursor: 'pointer',
                '&:hover': { color: '#F7A600' }
              }}
              onClick={() => handlePercentageClick(value)}
            >
              {value}%
            </Typography>
          ))}
        </Box>
      </Box>

      {/* Order Preview */}
      {distributedOrders.length > 0 && (
        <Box sx={{ 
          bgcolor: '#181A20', 
          borderRadius: 1, 
          p: 1, 
          mb: 1.5,
          border: '1px solid #2B3139',
          maxHeight: '120px',
          overflow: 'auto'
        }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px', mb: 1, display: 'block' }}>
            Order Distribution Preview
          </Typography>
          {distributedOrders.map((order, index) => (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ color: orderType === 'Buy' ? '#0ECB81' : '#F6465D', fontSize: '10px' }}>
                {order.price}
              </Typography>
              <Typography sx={{ color: 'white', fontSize: '10px' }}>
                {order.quantity}
              </Typography>
              <Typography sx={{ color: '#848E9C', fontSize: '10px' }}>
                {order.value} USDT
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Add Scaled Orders Button */}
      <Button
        variant="outlined"
        startIcon={<Add />}
        fullWidth
        onClick={addScaledOrders}
        disabled={!distributedOrders.length}
        sx={{
          color: distributedOrders.length ? '#F7A600' : '#848E9C',
          borderColor: distributedOrders.length ? '#F7A600' : '#2B3139',
          fontSize: '12px',
          py: 0.8,
          mb: 1.5,
          height: '32px',
          '&:hover': {
            borderColor: '#F7A600',
            color: '#F7A600',
            bgcolor: 'rgba(247, 166, 0, 0.1)'
          }
        }}
      >
        Add Scaled Orders ({distributedOrders.length})
      </Button>

      {/* Active Orders List */}
      {scaledOrders.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
            Active Orders ({scaledOrders.length})
          </Typography>
          <Box sx={{ 
            bgcolor: '#181A20', 
            borderRadius: 1, 
            border: '1px solid #2B3139',
            maxHeight: '100px',
            overflow: 'auto'
          }}>
            {scaledOrders.map((order, index) => (
              <Box key={index} sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                p: 1,
                borderBottom: index < scaledOrders.length - 1 ? '1px solid #2B3139' : 'none'
              }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ color: orderType === 'Buy' ? '#0ECB81' : '#F6465D', fontSize: '10px' }}>
                    {order.price} • {order.quantity} • {order.value} USDT
                  </Typography>
                </Box>
                <IconButton 
                  size="small" 
                  onClick={() => removeOrder(index)}
                  sx={{ color: '#848E9C', p: 0.5 }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Options */}
      <Box sx={{ mb: 1.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={postOnly}
              onChange={(e) => setPostOnly(e.target.checked)}
              size="small"
              sx={{ 
                color: '#848E9C',
                '&.Mui-checked': { color: '#F7A600' },
                padding: '4px'
              }}
            />
          }
          label={
            <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>
              Post-Only
            </Typography>
          }
          sx={{ ml: 0, mb: 0.5 }}
        />
        {tradingType === 'Futures' && (
          <FormControlLabel
            control={
              <Checkbox
                checked={reduceOnly}
                onChange={(e) => setReduceOnly(e.target.checked)}
                size="small"
                sx={{ 
                  color: '#848E9C',
                  '&.Mui-checked': { color: '#F7A600' },
                  padding: '4px'
                }}
              />
            }
            label={
              <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>
                Reduce-Only
              </Typography>
            }
            sx={{ ml: 0 }}
          />
        )}
      </Box>

      {/* Order Summary */}
      {scaledOrders.length > 0 && (
        <Box sx={{ 
          bgcolor: '#181A20', 
          borderRadius: 1, 
          p: 1, 
          mb: 1.5,
          border: '1px solid #2B3139'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
              Total Orders
            </Typography>
            <Typography variant="caption" sx={{ color: '#0ECB81', fontSize: '10px', fontWeight: 500 }}>
              {scaledOrders.length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
              Total Value
            </Typography>
            <Typography variant="caption" sx={{ color: '#0ECB81', fontSize: '10px', fontWeight: 500 }}>
              {formatPrice(orderValue)} USDT
            </Typography>
          </Box>
        </Box>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={orderType !== 'Buy' || scaledOrders.length === 0 || isSubmitting}
          sx={{
            bgcolor: orderType === 'Buy' ? '#0ECB81' : '#1E2329',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            py: 1,
            height: '36px',
            '&:hover': { 
              bgcolor: orderType === 'Buy' ? '#0BB76D' : '#2A2E39'
            },
            '&:disabled': {
              bgcolor: '#2B3139',
              color: '#848E9C'
            }
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={16} sx={{ color: 'white' }} />
          ) : (
            `${tradingType === 'Futures' ? 'Long' : 'Buy'} ${currentPair}`
          )}
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={orderType !== 'Sell' || scaledOrders.length === 0 || isSubmitting}
          sx={{
            bgcolor: orderType === 'Sell' ? '#F6465D' : '#1E2329',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            py: 1,
            height: '36px',
            '&:hover': { 
              bgcolor: orderType === 'Sell' ? '#E13B52' : '#2A2E39'
            },
            '&:disabled': {
              bgcolor: '#2B3139',
              color: '#848E9C'
            }
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={16} sx={{ color: 'white' }} />
          ) : (
            `${tradingType === 'Futures' ? 'Short' : 'Sell'} ${currentPair}`
          )}
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmation}
        onClose={cancelOrder}
        PaperProps={{
          sx: {
            bgcolor: '#1E2329',
            color: 'white',
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #2B3139', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: '#F7A600' }} />
            <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
              Confirm {orderType} Order
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: '#848E9C', fontSize: '12px', mb: 1 }}>
              Order Details:
            </Typography>
            <Box sx={{ bgcolor: '#181A20', p: 1.5, borderRadius: 1, border: '1px solid #2B3139' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>Type:</Typography>
                <Typography sx={{ color: 'white', fontSize: '11px' }}>Scaled Order</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>Side:</Typography>
                <Typography sx={{ color: orderType === 'Buy' ? '#0ECB81' : '#F6465D', fontSize: '11px' }}>
                  {orderType}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>Symbol:</Typography>
                <Typography sx={{ color: 'white', fontSize: '11px' }}>{currentPair}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>Total Orders:</Typography>
                <Typography sx={{ color: 'white', fontSize: '11px' }}>{scaledOrders.length}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>Total Value:</Typography>
                <Typography sx={{ color: '#0ECB81', fontSize: '11px', fontWeight: 500 }}>
                  {formatPrice(orderValue)} USDT
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>Duration:</Typography>
                <Typography sx={{ color: 'white', fontSize: '11px' }}>
                  {selectedPlan?.name || 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>Price Range:</Typography>
                <Typography sx={{ color: 'white', fontSize: '11px' }}>
                  {formatPrice(minPrice)} - {formatPrice(maxPrice)}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>
            Are you sure you want to place this scaled order? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #2B3139', pt: 1 }}>
          <Button
            onClick={cancelOrder}
            sx={{
              color: '#848E9C',
              '&:hover': { bgcolor: 'rgba(132, 142, 156, 0.1)' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmOrder}
            disabled={isSubmitting}
            sx={{
              bgcolor: orderType === 'Buy' ? '#0ECB81' : '#F6465D',
              color: 'white',
              '&:hover': {
                bgcolor: orderType === 'Buy' ? '#0BB76D' : '#E13B52'
              },
              '&:disabled': {
                bgcolor: '#2B3139',
                color: '#848E9C'
              }
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={16} sx={{ color: 'white' }} />
            ) : (
              `Confirm ${orderType}`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScaledOrder;
